import type { Asset } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import TMMButton from '#/common/components/TMMButton';
import { gql } from '@apollo/client';
import {
  getAssetTypeList,
  getBuildingList,
  getManufacturerList,
  getModelList,
  getTenantList,
} from '#/graphql/queries';
import { conditionsList, matchStatuses, RAW_MATCH_STATUS_FSM } from '../../utils/constants';
import { states } from '../../utils/mappings';
import {
  SelectLabel,
  MatchingItemLabel,
  MatchingItemCell,
} from '../../utils/MatchingColumnsStyles';
import { dataSourceList, dataSources } from '#/modules/admin/tenants/utils/mappings';

export interface AssetDataGridColumnsOptions {
  onEditMatch?: (asset: Asset) => void;
  isTechnician?: boolean;
}

export const createAssetColumns = (
  options: AssetDataGridColumnsOptions = {},
): ColumnProps<Asset>[] => [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'displayNumber',
    caption: 'Number',
    dataType: 'string',
    visible: true,
    fixed: true,
    openOnClick: true,
    sortIndex: 0,
    sortOrder: 'desc',
  },
  {
    dataField: 'assetTag',
    caption: 'Asset Tag',
    dataType: 'string',
    visible: true,
    openOnClick: true,
  },
  {
    dataField: 'assetType.name',
    caption: 'Asset Type',
    dataType: 'string',
    visible: true,
    headerFilter: {
      getItemList: gql(getAssetTypeList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'manufacturer.name',
    caption: 'Manufacturer',
    dataType: 'string',
    visible: true,
    headerFilter: {
      getItemList: gql(getManufacturerList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'location.id',
    caption: 'Location',
    dataType: 'string',
    visible: true,
    filterField: 'location.name',
    headerFilter: {
      getItemList: gql(getBuildingList),
      displayExpr: 'name',
      valueExpr: 'id',
    },
    cellRender: (e) => e.data?.location?.name || '',
  },
  {
    dataField: 'model.name',
    caption: 'Model',
    dataType: 'string',
    visible: true,
    headerFilter: {
      getItemList: gql(getModelList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'state',
    caption: 'Status',
    dataType: 'string',
    visible: true,
    lookup: {
      nullable: true,
      dataSource: Object.values(states),
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    dataType: 'string',
    visible: true,
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'serialNumber',
    caption: 'Serial Number',
    dataType: 'string',
    visible: true,
  },
  {
    dataField: 'woCount',
    caption: 'Work Orders Count',
    dataType: 'number',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
  },
  {
    dataField: 'findingsCount',
    caption: 'Findings Count',
    dataType: 'number',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
  },
  {
    dataField: 'installDate',
    caption: 'Install Date',
    dataType: 'date',
  },
  {
    dataField: 'condition',
    caption: 'Asset Condition',
    dataType: 'string',
    allowFiltering: false,
    lookup: {
      nullable: true,
      dataSource: conditionsList,
      displayExpr: 'text',
      valueExpr: 'value',
    },
  },
  {
    dataField: 'matchedAsset',
    caption: 'Matching Asset',
    dataType: 'string',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
    visible: true,
    width: 100,
    cellRender: (e) => {
      const item = e.data;
      const matchedAsset = item?.matchedAsset;
      const isFsmDataSource =
        item?.tenant?.dataSource === matchStatuses.fsm.key && !!item?.tenant?.dataSourceId;

      if (
        (item.matchStatus === matchStatuses.fsm.key ||
          item.matchStatus === matchStatuses.matched.key) &&
        isFsmDataSource
      ) {
        const label = matchedAsset ?? <SelectLabel>Select</SelectLabel>;

        if (!options.onEditMatch || options.isTechnician) {
          return <MatchingItemLabel>{label}</MatchingItemLabel>;
        }

        return (
          <MatchingItemCell>
            <MatchingItemLabel>{label}</MatchingItemLabel>
            <TMMButton icon="pen" type="secondary" onClick={() => options.onEditMatch?.(item)} />
          </MatchingItemCell>
        );
      }
    },
  },
  {
    dataField: 'isParent',
    caption: 'Is Parent',
    dataType: 'boolean',
  },
  {
    dataField: 'replacementCost',
    caption: 'Replacement Cost',
    dataType: 'number',
  },
  {
    dataField: 'matchStatus',
    caption: 'Source',
    dataType: 'string',
    visible: false,
    allowFiltering: false,
    lookup: {
      nullable: true,
      dataSource: dataSourceList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    calcFilterExpression: (value, operation) => {
      // operation is always equals as we support only lookup filter
      if (value === RAW_MATCH_STATUS_FSM) {
        // Bypass the Source grouping below - match the literal DB value (still-unmatched FSM asset)
        return ['matchStatus', '=', matchStatuses.fsm.key];
      } else if (value === dataSources.fsm.key) {
        // both matched and FSM
        return ['thirdPartyId2', '<>', null];
      } else if (value === dataSources.tis.key) {
        // only TIS
        return [['thirdPartyId', '<>', null], 'and', ['thirdPartyId2', '=', null]];
      }
      // no TIS, no FSM
      return [['thirdPartyId', '=', null], 'and', ['thirdPartyId2', '=', null]];
    },
    cellRender: (e) => {
      const item = e.data;
      const matchStatus = item?.matchStatus;
      if (matchStatus === matchStatuses.fsm.key || matchStatus === matchStatuses.matched.key) {
        return 'FSM';
      } else if (!matchStatus) {
        return '-';
      } else {
        return 'TC';
      }
    },
  },
];

const columns = createAssetColumns();

export default columns;
